/**
 * Citation authority resolution for the FAR chatbot citation renderer.
 *
 * The backend enriches bare citation tokens into rich `Citation` objects.
 * Non-FAR regulatory *supplements* ingested via the Reader A lane (currently
 * the FSS ordering procedures of subpart 538.71) are classified by the
 * upstream enricher as FAR atoms (their `538.*` section ids match the
 * FAR-shaped token regex), so they arrive with `kind: 'far'`. To render them
 * correctly — a `subpart 538.71`-tagged hover header instead of a wrong `FAR`
 * one — the renderer branches on the section numbering here rather than
 * special-casing one string.
 *
 * "subpart 538.71" is the RFO's own term (Part 8 §8.401(b): "ordering
 * procedures established by GSA and found at subpart 538.71"); we deliberately
 * do NOT invent a "GSAR" acronym the RFO never uses, and keep this label in
 * sync with the backend manifest (`data/rfo_anchors/supplements.json`
 * `authority_label`) and reference-block renderer.
 *
 * The hyperlink itself is already correct: the backend re-points these
 * citations at the supplement's source page URL, so `Citation.url` is the
 * right target and the existing `source === 'rfo' && url` link logic applies
 * unchanged.
 */

import type { Citation } from './types';

/**
 * subpart 538.71 (FSS ordering procedures) section ids — e.g. `538.7103-3`,
 * `538.7102-2`. The leading `538.` is the stable signal: the supplement
 * manifest (`data/rfo_anchors/supplements.json`) declares the section id
 * pattern `^538\.\d`.
 */
const SUPPLEMENT_SECTION_RE = /^538\.\d/;

/**
 * The RFO's own source tag for these ordering-procedure sections — kept in
 * sync with the backend manifest `authority_label`. No invented acronym.
 */
export const SUPPLEMENT_538_71_LABEL = 'subpart 538.71';

/**
 * True when the citation belongs to the Legacy (pre-RFO) FAR corpus. Legacy
 * citations are disambiguated by their carried provenance — `kind:
 * 'legacy_far'` (and/or `source: 'legacy'`) — NOT by section number, since
 * legacy and RFO share the same numbering (legacy-far-readers spec §4.6).
 */
export function isLegacyFarCitation(
  citation: Partial<Pick<Citation, 'kind' | 'source'>>
): boolean {
  return citation.kind === 'legacy_far' || citation.source === 'legacy';
}

/**
 * True when the citation's section belongs to the ingested subpart-538.71
 * supplement. (Name retained for back-compat with existing call sites; it is a
 * pure detection helper keyed on the `^538\.\d` numbering, not on any label.)
 */
export function isGsarCitation(citation: Pick<Citation, 'section'>): boolean {
  const section = citation.section;
  return typeof section === 'string' && SUPPLEMENT_SECTION_RE.test(section.trim());
}

/**
 * The regulation source tag for a citation's hover header.
 *
 * Returns `'subpart 538.71'` for ingested supplement sections (which arrive
 * with `kind: 'far'`), otherwise the FAR/DFARS/PGI mapping the renderer
 * already uses. Unknown / memo kinds fall through to `null` so the caller
 * keeps its canonical-based header.
 */
export function citationAuthorityLabel(
  citation: Pick<Citation, 'section' | 'kind'> &
    Partial<Pick<Citation, 'source'>>
): 'FAR' | 'Legacy FAR' | 'subpart 538.71' | 'DFARS' | 'PGI' | null {
  // Legacy FAR is checked first: provenance is carried, never inferred from
  // the section id (legacy/RFO share numbering — §4.6).
  if (isLegacyFarCitation(citation)) return 'Legacy FAR';
  if (isGsarCitation(citation)) return SUPPLEMENT_538_71_LABEL;
  const kind = citation.kind ?? 'far';
  if (kind === 'far') return 'FAR';
  if (kind === 'dfars') return 'DFARS';
  if (kind === 'pgi') return 'PGI';
  return null;
}
